import * as React from 'react'
import {createContext, useContext, useEffect, useMemo, type ReactNode} from 'react'
import {
  environmentTitle,
  webEnvironmentFaviconSlug,
  webEnvironmentFromHostname,
  webEnvironmentLabel,
  type WebEnvironment
} from 'notability-services-common-universal'
import {useIsClientSide} from './hooks/useIsClientSide'

/**
 * The environment the page is running in, from its hostname. `undefined` on the server and during
 * hydration, so server-rendered chrome always matches what the client first paints; the client
 * value lands in the render right after.
 */
export function useWebEnvironment(): WebEnvironment | undefined {
  const isClientSide = useIsClientSide()
  return useMemo(
    () => (isClientSide ? webEnvironmentFromHostname(window.location.hostname) : undefined),
    [isClientSide]
  )
}

export interface EnvironmentChromeOptions {
  /** The backend the page talks to, so a dev server shows which deployment it is pointed at. */
  apiServiceUrl?: string
  /** Baked into preview builds; see `Dockerfile.preview`. */
  previewPrNumber?: string
  /** Where this service serves an environment's favicons from; defaults to {@link deployedFaviconDirectory}. */
  faviconDirectory?: (slug: string, environment: WebEnvironment) => string
}

/**
 * Staging and berries read marketing's copy behind nginx's `/static/`, since the other Next
 * services serve nothing at the origin root there; a dev server or preview serves its own `public/`.
 */
export function deployedFaviconDirectory(slug: string, environment: WebEnvironment): string {
  const behindNginx = environment.kind === 'staging' || environment.kind === 'berry'
  return behindNginx ? `/static/favicons/${slug}` : `/favicons/${slug}`
}

/** Must match `SIZES` in `scripts/build-favicons.mjs`, which rasterises them. */
const FAVICON_SIZES = [96, 32]

/**
 * Swaps the page's favicon for an environment's. The production `<link>`s come from `<FaviconLinks />`,
 * and Next's head manager only re-emits intrinsic elements that are direct `next/head` children, so
 * once replaced they stay replaced across client-side navigations.
 */
export function replaceFavicons(doc: Document, directory: string): void {
  for (const link of Array.from(doc.head.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]'))) {
    link.remove()
  }
  for (const size of FAVICON_SIZES) {
    const link = doc.createElement('link')
    link.rel = 'icon'
    link.type = 'image/png'
    link.setAttribute('sizes', `${size}x${size}`)
    link.href = `${directory}/favicon-${size}x${size}.png`
    doc.head.appendChild(link)
  }
}

/**
 * Brands the browser chrome for a non-production environment: swaps the favicon and returns the
 * label to slot into the page title (`Staging`, `Boysenberry`, `Local → Staging`, `Preview #123`).
 * Returns `undefined` in production and before hydration, when nothing is decorated.
 */
export function useEnvironmentChrome({
  apiServiceUrl,
  previewPrNumber,
  faviconDirectory = deployedFaviconDirectory
}: EnvironmentChromeOptions): string | undefined {
  const environment = useWebEnvironment()

  useEffect(() => {
    if (!environment) return
    const slug = webEnvironmentFaviconSlug(environment)
    if (!slug) return
    replaceFavicons(document, faviconDirectory(slug, environment))
  }, [environment, faviconDirectory])

  return environment && webEnvironmentLabel(environment, {apiServiceUrl, previewPrNumber})
}

const EnvironmentLabelContext = createContext<string | undefined>(undefined)

/**
 * Mount once per Next service in `_app`. Pages then read the label through
 * {@link useEnvironmentTitle} without each knowing the service's config.
 */
export function EnvironmentChromeProvider({children, ...options}: EnvironmentChromeOptions & {children: ReactNode}) {
  const label = useEnvironmentChrome(options)
  return <EnvironmentLabelContext.Provider value={label}>{children}</EnvironmentLabelContext.Provider>
}

/** A page title with the environment slotted in ahead of the brand — see `environmentTitle`. */
export function useEnvironmentTitle(title: string | undefined): string | undefined {
  const label = useContext(EnvironmentLabelContext)
  return title === undefined ? undefined : environmentTitle(title, label)
}
